/**
 * Material/colors design tokens from Figma (Queen One Design System).
 * Source: material/colors variable collection.
 */

import { alpha } from "@mui/material/styles";

// These colors are base colors ranging from 50-600.
export const materialColors = {
  onyx: {
    50: "#FFFFFF",
    100: "#F5F0F9",
    200: "#C0B6C7",
    300: "#93869C",
    400: "#685E6F",
    500: "#3A3341",
    600: "#252327",
  },
  violet: {
    50: "#EAB1FC",
    100: "#C085F5",
    200: "#A451F1",
    300: "#8900EE",
    400: "#7700EE",
    500: "#5E07B4",
    600: "#51138E",
  },
  amber: {
    50: "#FFF8E1",
    100: "#FFECB3",
    200: "#FFE082",
    300: "#FFD54F",
    400: "#FFCA28",
    500: "#FFC107",
    600: "#FFB300",
  },
  pink: {
    50: "#FCE4EC",
    100: "#F8BBD0",
    200: "#F48FB1",
    300: "#F06292",
    400: "#EC407A",
    500: "#E91E63",
    600: "#D81B60",
  },
  blue: {
    50: "#EBF1FE",
    100: "#BED3FC",
    200: "#9AB6EF",
    300: "#7399E5",
    400: "#5085EF",
    500: "#3675F4",
    600: "#3E65B4",
  },
  green: {
    50: "#DEF7F1",
    100: "#A6F4E2",
    200: "#5EE4C5",
    300: "#25C19D",
    400: "#1D987C",
    500: "#287D6A",
    600: "#2F5F4F",
  },
  red: {
    50: "#FEEBEE",
    100: "#FCC4BE",
    200: "#EF9A9A",
    300: "#E57373",
    400: "#EF5350",
    500: "#F44336",
    600: "#B4483E",
  },

  // Paper elevation base colors.
  moonlight: {
    50: "#FFFFFF",
    100: "#FDFBFE",
    200: "#F5F0F9",
    300: "#E9E2EF",
    400: "#E1DBE7",
    500: "#D7D0DD",
    600: "#CDC6D4",
    700: "#C9C1D2",
    800: "#C4BBCE",
    900: "#BCB2C7",
    1000: "#B4A9C1",
    1100: "#AA9DB8",
    1200: "#9D91AC",
    1300: "#8B8099",
    1400: "#3A3341",
    1500: "#252327",
  },
} as const;

const c = materialColors;

/**
 * Opacity variants. Pre-computed with alpha() so values are ready-to-use color strings.
 */
export const materialColorsOpacity = {
  onyx: {
    "50-4%": alpha(c.onyx[50], 0.04),
    "50-8%": alpha(c.onyx[50], 0.08),
    "50-12%": alpha(c.onyx[50], 0.12),
    "50-24%": alpha(c.onyx[50], 0.24),
    "50-40%": alpha(c.onyx[50], 0.4),
    "50-50%": alpha(c.onyx[50], 0.5),
    "400-4%": alpha(c.onyx[400], 0.04),
    "400-8%": alpha(c.onyx[400], 0.08),
    "400-12%": alpha(c.onyx[400], 0.12),
    "400-24%": alpha(c.onyx[400], 0.24),
    "400-40%": alpha(c.onyx[400], 0.4),
    "400-50%": alpha(c.onyx[400], 0.5),
    "400-60%": alpha(c.onyx[400], 0.6),
    "600-4%": alpha(c.onyx[600], 0.04),
    "600-8%": alpha(c.onyx[600], 0.08),
    "600-12%": alpha(c.onyx[600], 0.12),
    "600-24%": alpha(c.onyx[600], 0.24),
    "600-40%": alpha(c.onyx[600], 0.4),
    "600-50%": alpha(c.onyx[600], 0.5),
  },
  violet: {
    "50-15%": alpha(c.violet[50], 0.15),
    "200-24%": alpha(c.violet[200], 0.24),
    "400-4%": alpha(c.violet[400], 0.04),
    "400-8%": alpha(c.violet[400], 0.08),
    "400-12%": alpha(c.violet[400], 0.12),
    "400-24%": alpha(c.violet[400], 0.24),
    "400-40%": alpha(c.violet[400], 0.4),
    "400-50%": alpha(c.violet[400], 0.5),
    "400-60%": alpha(c.violet[400], 0.6),
  },
  amber: {
    "400-4%": alpha(c.amber[400], 0.04),
    "400-8%": alpha(c.amber[400], 0.08),
    "400-12%": alpha(c.amber[400], 0.12),
    "400-24%": alpha(c.amber[400], 0.24),
    "400-40%": alpha(c.amber[400], 0.4),
    "400-50%": alpha(c.amber[400], 0.5),
    "400-60%": alpha(c.amber[400], 0.6),

    // Exception on the 700-opacity values.
    "700-4%": alpha("#EF6C00", 0.04),
    "700-8%": alpha("#EF6C00", 0.08),
    "700-12%": alpha("#EF6C00", 0.12),
    "700-24%": alpha("#EF6C00", 0.24),
    "700-40%": alpha("#EF6C00", 0.4),
    "700-50%": alpha("#EF6C00", 0.5),
    "700-60%": alpha("#EF6C00", 0.6),
  },
  pink: {
    "300-4%": alpha(c.pink[300], 0.04),
    "300-8%": alpha(c.pink[300], 0.08),
    "300-12%": alpha(c.pink[300], 0.12),
    "300-24%": alpha(c.pink[300], 0.24),
    "300-40%": alpha(c.pink[300], 0.4),
    "300-50%": alpha(c.pink[300], 0.5),
    "300-60%": alpha(c.pink[300], 0.6),
  },
  blue: {
    "300-4%": alpha(c.blue[300], 0.04),
    "300-8%": alpha(c.blue[300], 0.08),
    "300-12%": alpha(c.blue[300], 0.12),
    "300-24%": alpha(c.blue[300], 0.24),
    "300-40%": alpha(c.blue[300], 0.4),
    "300-50%": alpha(c.blue[300], 0.5),
    "300-60%": alpha(c.blue[300], 0.6),
  },
  green: {
    "200-4%": alpha(c.green[200], 0.04),
    "200-8%": alpha(c.green[200], 0.08),
    "200-12%": alpha(c.green[200], 0.12),
    "200-24%": alpha(c.green[200], 0.24),
    "200-40%": alpha(c.green[200], 0.4),
    "200-50%": alpha(c.green[200], 0.5),
    "200-60%": alpha(c.green[200], 0.6),
  },
  red: {
    "400-4%": alpha(c.red[400], 0.04),
    "400-8%": alpha(c.red[400], 0.08),
    "400-12%": alpha(c.red[400], 0.12),
    "400-24%": alpha(c.red[400], 0.24),
    "400-40%": alpha(c.red[400], 0.4),
    "400-50%": alpha(c.red[400], 0.5),
    "400-60%": alpha(c.red[400], 0.6),
  },
  moonlight: {
    "1400-80%": alpha(c.moonlight[1400], 0.8),
  },
} as const;
